import type {
  CopilotInvokeRequestContentBlocksInner,
  CopilotResourceRef,
  HumanMessageContentBlocksInner,
  ResourceRef,
} from "src/clients/agent-management";
import {
  parseReferenceContent,
  serializeReference,
} from "src/unifiedCopilot/chat/contextualEntityReferenceContent";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const parseCopilotResourceRefValue = (
  value: unknown,
): CopilotResourceRef | null => {
  if (
    !isRecord(value) ||
    typeof value.type !== "string" ||
    typeof value.id !== "string"
  ) {
    return null;
  }

  if (value.parent === undefined || value.parent === null) {
    return { type: value.type, id: value.id };
  }

  // Ancestor chains validate recursively (e.g. node → flow-version → flow);
  // one malformed level invalidates the whole ref.
  const parent = parseCopilotResourceRefValue(value.parent);
  if (parent === null) {
    return null;
  }

  return { type: value.type, id: value.id, parent };
};

export const parseCopilotResourceRef = (
  serializedRef: string | undefined,
): CopilotResourceRef | null => {
  if (serializedRef === undefined) {
    return null;
  }
  try {
    const value: unknown = JSON.parse(serializedRef);
    return parseCopilotResourceRefValue(value);
  } catch {
    return null;
  }
};

const appendContentBlock = (
  blocks: CopilotInvokeRequestContentBlocksInner[],
  block: CopilotInvokeRequestContentBlocksInner,
): CopilotInvokeRequestContentBlocksInner[] => {
  const previous = blocks.at(-1);
  if (previous?.type === "text" && block.type === "text") {
    return [
      ...blocks.slice(0, -1),
      { type: "text", text: `${previous.text}${block.text}` },
    ];
  }
  return [...blocks, block];
};

export const mapPromptContentToCopilotContentBlocks = (
  content: string,
): CopilotInvokeRequestContentBlocksInner[] =>
  parseReferenceContent(content.trim())
    .map((part): CopilotInvokeRequestContentBlocksInner => {
      if (part.type === "text") {
        return { type: "text", text: part.text };
      }

      const ref = parseCopilotResourceRef(part.attributes.resourceRef);
      return ref !== null && part.attributes.label
        ? { type: "mention", ref, label: part.attributes.label }
        : {
            type: "text",
            text: part.attributes.label ?? part.raw,
          };
    })
    .reduce(appendContentBlock, []);

/** A file attached to the composed message, uploaded before send. */
export type ChatAttachmentPayload = {
  fileId: string;
  name: string;
  fileType: string | null;
};

export const mapAttachmentsToCopilotFileBlocks = (
  attachments: ChatAttachmentPayload[],
): CopilotInvokeRequestContentBlocksInner[] =>
  attachments.map((attachment) => ({
    type: "file",
    file_id: attachment.fileId,
    name: attachment.name,
    file_type: attachment.fileType,
  }));

// The serialized ancestor chain, without any unknown wire extras.
const pickResourceRef = (ref: ResourceRef): ResourceRef => ({
  type: ref.type,
  id: ref.id,
  ...(ref.parent ? { parent: pickResourceRef(ref.parent) } : {}),
});

/**
 * The mention's `<reference/>` tag form for the transcript renderer. The
 * `resourceRef` attribute carries the full ref (incl. parent) so the rendered
 * pill can resolve a deeplink.
 */
export const mentionBlockToReferenceMarkdown = (block: {
  label: string;
  ref: ResourceRef;
}): string =>
  serializeReference({
    label: block.label,
    id: `${block.ref.type}:${block.ref.id}`,
    kind: block.ref.type,
    resourceRef: JSON.stringify(pickResourceRef(block.ref)),
  });

export const humanMessageContentBlocksToMarkdown = (
  blocks: HumanMessageContentBlocksInner[],
): string =>
  blocks
    .map((block) => {
      switch (block.type) {
        case "text":
          return block.text;
        case "mention":
          return mentionBlockToReferenceMarkdown(block);
        // File blocks render as attachment cards outside the bubble.
        case "file":
          return "";
      }
    })
    .join("");
